import { Divider, Flex, FormControl, FormErrorMessage } from '@chakra-ui/react';
import { Form, Formik, FormikErrors } from 'formik';
import React from 'react';
import { useSnackbar } from 'ui-components';
import Button from '~/components/Button';
import PasswordInput from '~/components/PasswordInput';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';

import { CrossIcon } from '../../../icons';
import { ShareModal } from '../Modal';

interface FormValues {
  password: string;
  passwordRepeat: string;
}

interface Props {
  hasPassword?: boolean;
  onCancel: () => void;
  onSubmit: (password: string) => void;
  onRemove: () => void;
}

const validate = ({ password, passwordRepeat }: FormValues) => {
  let errors: FormikErrors<FormValues> = {};

  if (!password) {
    errors.password = 'Please enter a password';
  }

  if (password !== passwordRepeat) {
    errors.passwordRepeat = "The passwords don't match";
  }

  return errors;
};

export const PasswordFormModal: React.FC<Props> = ({
  onSubmit,
  onCancel,
  hasPassword = false,
  onRemove,
}) => {
  const snackbar = useSnackbar();

  const handleFinalSubmit = async ({ password }: FormValues) => {
    try {
      await onSubmit(password);

      snackbar.success({
        description: 'Password set: The page has been protected by password',
        duration: 9000,
        isCloseable: true,
      });
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  };

  const handleRemove = async () => {
    try {
      await onRemove();

      snackbar.success({
        description:
          'Password Removed: The password has been removed from your page',
        duration: 9000,
        isCloseable: true,
      });
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={{ password: '', passwordRepeat: '' }}
      onSubmit={handleFinalSubmit}
      validate={validate}
      validateOnBlur
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        isValid,
        dirty,
      }) => (
        <Form>
          <Flex justifyContent="space-between" alignItems="center">
            <ShareModal.Header flex="unset" padding={0}>
              {hasPassword ? 'Edit Password' : 'Add password'}
            </ShareModal.Header>
            <ShareModal.CloseButton
              position="relative"
              top={0}
              right={0}
              width={6}
              height={6}
              fontSize={0}
            >
              <CrossIcon width="10px" height="10px" color="primary.500" />
            </ShareModal.CloseButton>
          </Flex>

          <ShareModal.Body padding={0}>
            <Divider marginTop={4} />
            <FormControl
              isInvalid={!!errors.password && touched.password}
              marginTop={4}
            >
              <PasswordInput
                autoComplete="new-password"
                id="password"
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Add password"
                value={values.password}
              />
              {errors.password && touched.password && (
                <FormErrorMessage color="error.500" as="p">
                  {errors.password}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl
              isInvalid={!!errors.passwordRepeat && touched.passwordRepeat}
              marginTop={4}
            >
              <PasswordInput
                autoComplete="new-password"
                id="passwordRepeat"
                name="passwordRepeat"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Confirm password"
                value={values.passwordRepeat}
              />
              {errors.passwordRepeat && touched.passwordRepeat && (
                <FormErrorMessage color="error.500" as="p">
                  {errors.passwordRepeat}
                </FormErrorMessage>
              )}
            </FormControl>
          </ShareModal.Body>
          <ShareModal.Footer
            spaceBetween
            padding={0}
            marginTop={4}
            dividerProps={{ marginTop: 6 }}
          >
            {hasPassword ? (
              <Button
                colorScheme="error"
                variant="ghost"
                onClick={handleRemove}
                size="xs"
                marginLeft={-2}
              >
                Remove Password
              </Button>
            ) : (
              <Button
                colorScheme="primary"
                variant="ghost"
                onClick={onCancel}
                size="xs"
                marginLeft={-2}
              >
                Cancel
              </Button>
            )}

            <Button
              colorScheme="primary"
              isDisabled={!isValid || !dirty}
              size="xs"
              variant="ghost"
              type="submit"
              isLoading={isSubmitting}
              loadingText="Saving Password"
              marginRight={-2}
            >
              Save Password
            </Button>
          </ShareModal.Footer>
        </Form>
      )}
    </Formik>
  );
};
